
import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { ShoppingCart, Close, Favorite, FavoriteBorder, Info, Star } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

Modal.setAppElement('#root');

const CateringItems = ({ setShowLogin, parentId, categories, cateringPackages, activeCategory }) => {
    const [packages, setPackages] = useState([]);
    const [filteredPackages, setFilteredPackages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [miniCartItems, setMiniCartItems] = useState([]);
    const [isHovered, setIsHovered] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedTime, setSelectedTime] = useState("");
    const [isAllSelected, setIsAllSelected] = useState(true);
    const [isVegSelected, setIsVegSelected] = useState(false);
    const [isNonVegSelected, setIsNonVegSelected] = useState(false);
    const [isDrinksSelected, setIsDrinksSelected] = useState(false);
    const [activeTooltip, setActiveTooltip] = useState(null);
    const [cartQuantities, setCartQuantities] = useState({});
    const [hasInitialLoad, setHasInitialLoad] = useState(false);
    const [favorites, setFavorites] = useState([]);

    const [description, setDescription] = useState("");
    const navigate = useNavigate();

    // Check screen size
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Load cart items and favorites on component mount
    useEffect(() => {
        const initializeCart = async () => {
            await loadCartItems();
            await fetchCartData();
            loadFavorites();
            setHasInitialLoad(true);
        };
        initializeCart();
    }, []);

    // NEW EFFECT: Automatically show/hide cart modal based on cart items
    useEffect(() => {
        if (!hasInitialLoad) return;

        const hasCartItems = miniCartItems.length > 0 || Object.values(cartQuantities).some(qty => qty > 0);

        if (hasCartItems) {
            setIsModalOpen(true);
        } else {
            setIsModalOpen(false);
        }
    }, [miniCartItems, cartQuantities, hasInitialLoad]);

    // Fetch cart data from API
    const fetchCartData = async () => {
        try {
            const userId = localStorage.getItem("userId");
            const token = localStorage.getItem("authToken");

            if (!userId || !token) {
                console.log("No user ID or token, skipping API cart fetch");
                return;
            }

            const response = await axios.get(
                `http://10.10.20.9:7007/caterings-0.0.1-SNAPSHOT/api/user/cart/${userId}`,
                {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            if (response.data && Array.isArray(response.data) && response.data.length > 0) {
                const currentCart = JSON.parse(localStorage.getItem('cateringCart') || '[]');
                if (currentCart.length === 0) {
                    const quantities = {};
                    const cartItems = [];

                    response.data.forEach(order => {
                        order.items.forEach(item => {
                            const packageId = item.packageId;
                            const quantity = parseInt(item.quantity) || 0;
                            const price = parseFloat(item.price) || 0;

                            quantities[packageId] = quantity;

                            cartItems.push({
                                packageId: packageId,
                                packageName: item.packageName || 'Unknown Package',
                                quantity: quantity,
                                price: price,
                                image: item.image,
                                packageType: item.packageType,
                                vendorId: item.vendorId,
                                items: item.items || [],
                                cateringDate: item.cateringDate,
                                cateringTime: item.cateringTime,
                                addedAt: item.addedAt || new Date().toISOString()
                            });
                        });
                    });

                    setCartQuantities(quantities);
                    setMiniCartItems(cartItems);
                    localStorage.setItem('cateringCart', JSON.stringify(cartItems));
                }
            }
        } catch (error) {
            console.error("❌ Error fetching cart data:", error);
        }
    };

    // Load favorites from localStorage
    const loadFavorites = () => {
        try {
            const storedFavorites = JSON.parse(localStorage.getItem('cateringFavorites') || '[]');
            setFavorites(storedFavorites);
        } catch (error) {
            console.error("❌ Error loading favorites:", error);
            setFavorites([]);
        }
    };

    // Save favorites to localStorage
    const saveFavorites = (newFavorites) => {
        localStorage.setItem('cateringFavorites', JSON.stringify(newFavorites));
        setFavorites(newFavorites);
    };

    // Filter packages based on active category AND veg/non-veg/drinks filters
    useEffect(() => {
        if (!cateringPackages || !Array.isArray(cateringPackages) || cateringPackages.length === 0) {
            setFilteredPackages([]);
            return;
        }

        let filteredData = [...cateringPackages];

        // First filter by category
        if (activeCategory && activeCategory !== "Allitems") {
            filteredData = filteredData.filter((item) => {
                return item && item.packageType === activeCategory;
            });
        }

        // Then filter by veg/non-veg/drinks if selected
        if (!isAllSelected && Array.isArray(filteredData)) {
            if (isVegSelected) {
                filteredData = filteredData.filter((item) =>
                    item && (item.tag === "Veg" || item.packageType?.toLowerCase().includes('veg'))
                );
            } else if (isNonVegSelected) {
                filteredData = filteredData.filter((item) =>
                    item && (item.tag === "Non_Veg" || item.packageType?.toLowerCase().includes('non') || item.packageType?.toLowerCase().includes('non-veg'))
                );
            } else if (isDrinksSelected) {
                filteredData = filteredData.filter((item) =>
                    item && (item.tag === "Drinks" ||
                        item.packageType?.toLowerCase().includes('drink') ||
                        item.packageType?.toLowerCase().includes('beverage') ||
                        item.packageName?.toLowerCase().includes('drink') ||
                        item.packageName?.toLowerCase().includes('beverage'))
                );
            }
        }

        setFilteredPackages(Array.isArray(filteredData) ? filteredData : []);
    }, [activeCategory, cateringPackages, isVegSelected, isNonVegSelected, isDrinksSelected, isAllSelected]);

    // Load cart items from localStorage
    const loadCartItems = async () => {
        try {
            const cart = JSON.parse(localStorage.getItem('cateringCart') || '[]');

            // Validate and clean cart data
            const validatedCart = cart.filter(item =>
                item &&
                item.packageId &&
                !isNaN(parseFloat(item.price)) &&
                !isNaN(parseInt(item.quantity))
            ).map(item => ({
                ...item,
                price: parseFloat(item.price) || 0,
                quantity: parseInt(item.quantity) || 0
            }));

            setMiniCartItems(validatedCart);

            // Also set quantities from localStorage
            const quantities = {};
            validatedCart.forEach(item => {
                if (item.packageId) {
                    quantities[item.packageId] = parseInt(item.quantity) || 0;
                }
            });

            setCartQuantities(quantities);

        } catch (error) {
            console.error("❌ Error loading cart items:", error);
            setMiniCartItems([]);
            setCartQuantities({});
        }
    };

    // Helper function to remove from localStorage
    const removeFromLocalStorage = (packageId) => {
        const existingCart = JSON.parse(localStorage.getItem('cateringCart') || '[]');
        const updatedCart = existingCart.filter(item => item.packageId !== packageId);
        localStorage.setItem('cateringCart', JSON.stringify(updatedCart));
        setMiniCartItems(updatedCart);
        setCartQuantities(prev => {
            const newQuantities = { ...prev };
            delete newQuantities[packageId];
            return newQuantities;
        });
    };

    // Helper function to update localStorage quantity with validation
    const updateLocalStorageQuantity = (packageId, newQuantity) => {
        const existingCart = JSON.parse(localStorage.getItem('cateringCart') || '[]');
        const updatedCart = existingCart.map(item =>
            item.packageId === packageId
                ? {
                    ...item,
                    quantity: parseInt(newQuantity) || 0,
                    price: parseFloat(item.price) || 0
                }
                : item
        );
        localStorage.setItem('cateringCart', JSON.stringify(updatedCart));
        setMiniCartItems(updatedCart);
        setCartQuantities(prev => ({ ...prev, [packageId]: parseInt(newQuantity) || 0 }));
    };

    // DELETE API to remove item from cart
    const removeFromCart = async (packageId) => {
        try {
            const token = localStorage.getItem("authToken");
            const userId = localStorage.getItem("userId");

            if (!token || !userId) {
                alert("Please login to manage cart");
                return;
            }

            // Get cart data to find the correct cartId
            const cartResponse = await axios.get(
                `http://10.10.20.9:7007/caterings-0.0.1-SNAPSHOT/api/user/cart/${userId}`,
                {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            let cartId = null;

            if (cartResponse.data && Array.isArray(cartResponse.data)) {
                for (const order of cartResponse.data) {
                    const itemExists = order.items.find(item => item.packageId === packageId);
                    if (itemExists) {
                        cartId = order.id;
                        break;
                    }
                }
            }

            if (!cartId) {
                removeFromLocalStorage(packageId);
                alert("Item removed from local cart!");
                return;
            }

            const response = await axios.delete(
                `http://10.10.20.9:7007/caterings-0.0.1-SNAPSHOT/api/user/package/${packageId}/${cartId}`,
                {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            if (response.status === 200) {
                removeFromLocalStorage(packageId);
                alert("Item removed from cart!");
            }
        } catch (error) {
            console.error("❌ Error removing item:", error);
            removeFromLocalStorage(packageId);
            alert("Item removed from local cart (API error occurred)");
        }
    };

    // PUT API to update quantity
    const updateQuantity = async (packageId, newQuantity) => {
        if (newQuantity < 1) {
            removeFromCart(packageId);
            return;
        }

        try {
            const token = localStorage.getItem("authToken");
            const userId = localStorage.getItem("userId");

            if (!token || !userId) {
                alert("Please login to manage cart");
                return;
            }

            // Get cart data to find the correct cartId
            const cartResponse = await axios.get(
                `http://10.10.20.9:7007/caterings-0.0.1-SNAPSHOT/api/user/cart/${userId}`,
                {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            let cartId = null;

            if (cartResponse.data && Array.isArray(cartResponse.data)) {
                for (const order of cartResponse.data) {
                    const item = order.items.find(item => item.packageId === packageId);
                    if (item) {
                        cartId = order.id;
                        break;
                    }
                }
            }

            if (!cartId) {
                updateLocalStorageQuantity(packageId, newQuantity);
                return;
            }

            const response = await axios.put(
                `http://10.10.20.9:7007/caterings-0.0.1-SNAPSHOT/api/user/updatequantity/${cartId}`,
                {
                    quantity: newQuantity,
                    packageId: packageId
                },
                {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            if (response.status === 200) {
                updateLocalStorageQuantity(packageId, newQuantity);
            }
        } catch (error) {
            console.error("❌ Error updating quantity:", error);
            updateLocalStorageQuantity(packageId, newQuantity);
            alert("Quantity updated locally (API update failed)");
        }
    };

    // Increment quantity
    const incrementQuantity = (packageId) => {
        const currentQty = cartQuantities[packageId] || 0;
        const newQuantity = currentQty + 1;
        updateQuantity(packageId, newQuantity);
    };

    // Decrement quantity
    const decrementQuantity = (packageId) => {
        const currentQty = cartQuantities[packageId] || 0;
        if (currentQty <= 1) {
            removeFromCart(packageId);
            return;
        }
        const newQuantity = currentQty - 1;
        updateQuantity(packageId, newQuantity);
    };

    // Check if item is in cart
    const isItemInCart = (packageId) => {
        return cartQuantities[packageId] > 0 || miniCartItems.some(item => item.packageId === packageId);
    };

    // Get quantity for item
    const getItemQuantity = (packageId) => {
        return cartQuantities[packageId] ||
            miniCartItems.find(item => item.packageId === packageId)?.quantity ||
            0;
    };

    // Check if item is in favorites
    const isItemInFavorites = (packageId) => {
        return favorites.includes(packageId);
    };

    // Toggle favorite status
    const toggleFavorite = (packageId) => {
        let newFavorites;
        if (favorites.includes(packageId)) {
            newFavorites = favorites.filter(id => id !== packageId);
        } else {
            newFavorites = [...favorites, packageId];
        }
        saveFavorites(newFavorites);
    };

    const addToCartDirectly = async (packageItem) => {
        const userId = localStorage.getItem("userId");
        const token = localStorage.getItem("authToken");

        if (!userId || !token) {
            setShowLogin(true);
            return;
        }

        try {
            setLoading(true);

            // Set default date and time
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            const defaultDate = tomorrow.toISOString().split('T')[0];
            const defaultTime = "12:00";

            const bookingRequest = {
                userId: parseInt(userId),
                packageId: packageItem.id,
                quantity: 1,
                cateringDate: defaultDate,
                cateringTime: defaultTime
            };

            const response = await axios.post(
                "http://10.10.20.9:7007/caterings-0.0.1-SNAPSHOT/api/user/cart/add",
                bookingRequest,
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    }
                }
            );

            // Store in localStorage
            const cartData = {
                packageId: packageItem.id,
                packageName: packageItem.packageName,
                packageType: packageItem.packageType,
                price: parseFloat(packageItem.totalPrice) || 0,
                quantity: 1,
                image: packageItem.image,
                vendorId: packageItem.vendorId,
                items: packageItem.items,
                cateringDate: defaultDate,
                cateringTime: defaultTime,
                addedAt: new Date().toISOString(),
                cartId: response.data.id
            };

            const existingCart = JSON.parse(localStorage.getItem('cateringCart') || '[]');
            const existingItemIndex = existingCart.findIndex(item => item.packageId === packageItem.id);

            if (existingItemIndex >= 0) {
                existingCart[existingItemIndex].quantity += 1;
                if (!existingCart[existingItemIndex].image && packageItem.image) {
                    existingCart[existingItemIndex].image = packageItem.image;
                }
            } else {
                existingCart.push(cartData);
            }

            localStorage.setItem('cateringCart', JSON.stringify(existingCart));
            setMiniCartItems(existingCart);
            setCartQuantities(prev => ({
                ...prev,
                [packageItem.id]: (prev[packageItem.id] || 0) + 1
            }));

        } catch (error) {
            console.error("❌ Error adding to cart:", error);
            if (error.response?.status === 401) {
                alert("Session expired. Please login again.");
                setShowLogin(true);
            } else if (error.response?.data) {
                alert(`Failed to add package: ${error.response.data.message || 'Please try again.'}`);
            } else {
                alert("Failed to add package to cart. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    // Calculate total price
    const calculateTotal = () => {
        return miniCartItems.reduce((total, item) => {
            const price = parseFloat(item.price) || 0;
            const quantity = parseInt(item.quantity) || 0;
            return total + (price * quantity);
        }, 0);
    };

    // Calculate total items count
    const calculateTotalItems = () => {
        return miniCartItems.reduce((total, item) => {
            const quantity = parseInt(item.quantity) || 0;
            return total + quantity;
        }, 0);
    };

    // Handle view cart click for modal
    const handleViewCartClick = () => {
        navigate('/cateringscartdata', {
            state: {
                cartItems: miniCartItems,
                cartQuantities: cartQuantities
            }
        });
    };

    // Go to cart
    const goToCart = () => {
        navigate('/cateringscartdata');
    };

    // Add to favorites function
    const handleAddToFavorites = async (packageId) => {
        const userId = localStorage.getItem("userId");
        const token = localStorage.getItem("authToken");

        if (!userId) {
            alert("Please log in to add items to favorites.");
            return;
        }

        if (!token) {
            alert("Please log in to add items to favorites.");
            return;
        }

        const apiUrl = `http://10.10.20.9:6300/Mamaswebsite-0.0.1-SNAPSHOT/favourite/add/${userId}/${packageId}`;

        try {
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error("Failed to add item to favorites");
            }

            const responseData = await response.json();
            alert("Package added to favorites successfully!");
        } catch (error) {
            console.error("Error:", error);
            alert("Error adding package to favorites. Please try again.");
        }
    };

    // Filter handler
    const handleFilterClick = (type) => {
        if (type === "All") {
            setIsAllSelected(true);
            setIsVegSelected(false);
            setIsNonVegSelected(false);
            setIsDrinksSelected(false);
        } else if (type === "Veg") {
            setIsAllSelected(false);
            setIsVegSelected(true);
            setIsNonVegSelected(false);
            setIsDrinksSelected(false);
        } else if (type === "Non-Veg") {
            setIsAllSelected(false);
            setIsVegSelected(false);
            setIsNonVegSelected(true);
            setIsDrinksSelected(false);
        } else if (type === "Drinks") {
            setIsAllSelected(false);
            setIsVegSelected(false);
            setIsNonVegSelected(false);
            setIsDrinksSelected(true);
        }
    };

    // Tooltip handler
    const handleToggleTooltip = (packageId, packageDescription) => {
        if (activeTooltip === packageId) {
            setActiveTooltip(null);
            setDescription("");
        } else {
            setActiveTooltip(packageId);
            setDescription(packageDescription || "");
        }
    };

    // Close modal
    const closeModal = () => {
        setIsModalOpen(false);
    };

    if (loading && filteredPackages.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '40px', fontSize: '18px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p style={{ marginTop: '15px' }}>Loading catering packages...</p>
            </div>
        );
    }

    return (
        <div style={{ marginTop: '10px' }} className='catering-items-data'>
            <style>
                {`
                /* Professional Restaurant Card Design */
                .offers-containers {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
                    gap: 24px;
                    padding: 20px;
                    max-width: 1400px;
                    margin: 0 auto;
                }
                
                .offers-cards {
                    background: white;
                    border-radius: 16px;
                    overflow: hidden;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
                    transition: all 0.3s ease;
                    border: 1px solid #f0f0f0;
                    position: relative;
                    font-family: 'Inter', sans-serif;
                }
                
                .offers-cards:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
                }
                
                /* Professional Filter Tabs */
                .filter-tabs {
                    display: flex;
                    justify-content: center;
                    gap: 8px;
                    margin-bottom: 30px;
                    padding: 0 20px;
                    flex-wrap: wrap;
                }
                
                .filter-tab {
                    padding: 12px 24px;
                    border: none;
                    border-radius: 25px;
                    font-weight: 600;
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    background: white;
                    color: #666;
                    border: 2px solid #e0e0e0;
                    min-width: 100px;
                }
                
                .filter-tab.active {
                    background: #ee6521;
                    color: white;
                    border-color: #ee6521;
                    box-shadow: 0 4px 12px rgba(177, 93, 198, 0.3);
                }
                
                .filter-tab.veg.active {
                    background: #22c55e;
                    border-color: #22c55e;
                    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
                }
                
                .filter-tab.nonveg.active {
                    background: #ef4444;
                    border-color: #ef4444;
                    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
                }
                
                .filter-tab.drinks.active {
                    background: #3b82f6;
                    border-color: #3b82f6;
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
                }
                
                /* Card Image Section */
                .card-image-container {
                    position: relative;
                    height: 200px;
                    overflow: hidden;
                }
                
                .card-image {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    transition: transform 0.3s ease;
                }
                
                .offers-cards:hover .card-image {
                    transform: scale(1.05);
                }
                
                .card-badge {
                    position: absolute;
                    top: 12px;
                    left: 12px;
                    background: rgba(255, 255, 255, 0.95);
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: 600;
                    color: #ee6521;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                }
                
                /* Card Content */
                .card-content {
                    padding: 20px;
                }
                
                .card-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 12px;
                }
                
                .card-title {
                    font-size: 18px;
                    font-weight: 700;
                    color: #1a1a1a;
                    margin: 0;
                    line-height: 1.3;
                    flex: 1;
                }
                
                .card-price {
                    font-size: 20px;
                    font-weight: 700;
                    color: #ee6521;
                    display: flex;
                    align-items: center;
                    gap: 4px;
                }
                
                .card-description {
                    color: #666;
                    font-size: 14px;
                    line-height: 1.5;
                    margin-bottom: 16px;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
                
                .card-features {
                    display: flex;
                    gap: 12px;
                    margin-bottom: 16px;
                    flex-wrap: wrap;
                }
                
                .feature-tag {
                    background: #f8f9fa;
                    padding: 6px 12px;
                    border-radius: 8px;
                    font-size: 12px;
                    font-weight: 500;
                    color: #555;
                    border: 1px solid #e9ecef;
                }
                
                /* Action Buttons */
                .card-actions {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-top: 16px;
                }
                
                .quantity-controls {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    background: #f8f9fa;
                    padding: 8px 16px;
                    border-radius: 25px;
                    border: 2px solid #ee6521;
                }
                
                .quantity-btn {
                    background: #ee6521;
                    color: white;
                    border: none;
                    border-radius: 50%;
                    width: 28px;
                    height: 28px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    font-weight: bold;
                    transition: all 0.2s ease;
                }
                
                .quantity-btn:hover {
                    background: #9a4cb0;
                    transform: scale(1.1);
                }
                
                .quantity-display {
                    font-size: 16px;
                    font-weight: 700;
                    color: #ee6521;
                    min-width: 20px;
                    text-align: center;
                }
                
                .add-to-cart-btn {
                    background: #ee6521;
                    color: white;
                    border: none;
                    padding: 12px 24px;
                    border-radius: 8px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    box-shadow: 0 2px 8px rgba(177, 93, 198, 0.3);
                }
                
                .add-to-cart-btn:hover {
                    background: #9a4cb0;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(177, 93, 198, 0.4);
                }
                
                /* Mobile Responsive */
                @media (max-width: 768px) {
                    .offers-containers {
                        grid-template-columns: 1fr;
                        gap: 16px;
                        padding: 16px;
                    }
                    
                    .filter-tabs {
                        gap: 6px;
                        margin-bottom: 20px;
                    }
                    
                    .filter-tab {
                        padding: 10px 20px;
                        font-size: 13px;
                        min-width: 80px;
                    }
                    
                    .card-image-container {
                        height: 180px;
                    }
                }
                
                @media (min-width: 769px) and (max-width: 1024px) {
                    .offers-containers {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }
                
                @media (min-width: 1025px) {
                    .offers-containers {
                        grid-template-columns: repeat(3, 1fr);
                        gap: 28px;
                    padding: 24px;
                    max-width: 1400px;
                        margin: 0 auto;
                        width: 100%;
                        box-sizing: border-box;
                    }
                    
                    .offers-cards {
                        width: 100%;
                        max-width: 380px;
                        margin: 0 auto;
                    }
                }
                
                /* Custom Scrollbar for Modal */
                .modal-content::-webkit-scrollbar {
                    width: 6px;
                }
                
                .modal-content::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 10px;
                }
                
                .modal-content::-webkit-scrollbar-thumb {
                    background: #ee6521;
                    border-radius: 10px;
                }
                `}
            </style>

            {/* Professional Filter Tabs */}
            <div className="filter-tabs">
                <button
                    className={`filter-tab ${isAllSelected ? 'active' : ''}`}
                    onClick={() => handleFilterClick("All")}
                >
                    All Items
                </button>
                <button
                    className={`filter-tab veg ${isVegSelected ? 'active' : ''}`}
                    onClick={() => handleFilterClick("Veg")}
                >
                    🥗 Veg
                </button>
                <button
                    className={`filter-tab nonveg ${isNonVegSelected ? 'active' : ''}`}
                    onClick={() => handleFilterClick("Non-Veg")}
                >
                    🍗 Non-Veg
                </button>
                <button
                    className={`filter-tab drinks ${isDrinksSelected ? 'active' : ''}`}
                    onClick={() => handleFilterClick("Drinks")}
                >
                    🥤 Drinks
                </button>
            </div>

            {/* Grid Container */}
            <div className="offers-containers">
                {filteredPackages.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '60px 20px',
                        fontSize: '18px',
                        color: '#666',
                        background: '#f9fafb',
                        borderRadius: '16px',
                        border: '2px dashed #d1d5db',
                        gridColumn: '1 / -1',
                        margin: '20px'
                    }}>
                        <div style={{ 
                            fontSize: '64px', 
                            marginBottom: '20px',
                            opacity: 0.7
                        }}>📦</div>
                        <h3 style={{
                            color: '#4b5563',
                            marginBottom: '12px',
                            fontSize: '24px',
                            fontWeight: '600'
                        }}>
                            No Packages Found
                        </h3>
                        <p style={{
                            color: '#6b7280',
                            fontSize: '16px',
                            maxWidth: '400px',
                            margin: '0 auto',
                            lineHeight: '1.5'
                        }}>
                            Try adjusting your filters or check back later for new catering packages.
                        </p>
                    </div>
                ) : (
                    filteredPackages.map((item) => {
                        const packageId = item.id;
                        const packageName = item.packageName;
                        const packagePrice = item.totalPrice;
                        const packageImage = item.image;
                        const packageType = item.packageType;
                        const itemsList = item.items || [];
                        const packageDescription = item.description || "Delicious catering package perfect for your events and celebrations.";
                        const isVeg = item.tag === "Veg" || packageType?.toLowerCase().includes('veg');
                        const isNonVeg = item.tag === "Non_Veg" || packageType?.toLowerCase().includes('non');
                        const itemInCart = isItemInCart(packageId);
                        const itemQuantity = getItemQuantity(packageId);
                        const isFavorite = isItemInFavorites(packageId);

                        return (
                            <div key={packageId} className="offers-cards">
                                {/* Favorite Heart Icon */}
                                <div 
                                    style={{
                                        position: 'absolute',
                                        top: '12px',
                                        right: '12px',
                                        zIndex: 10,
                                        background: 'rgba(255, 255, 255, 0.95)',
                                        borderRadius: '50%',
                                        width: '40px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onClick={() => toggleFavorite(packageId)}
                                    onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 1)'}
                                    onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.95)'}
                                >
                                    {isFavorite ? (
                                        <Favorite style={{ color: '#e53e3e', fontSize: '22px' }} />
                                    ) : (
                                        <FavoriteBorder style={{ color: '#6b7280', fontSize: '22px' }} />
                                    )}
                                </div>

                                {/* Package Image */}
                                <div className="card-image-container">
                                    {packageImage ? (
                                        <img
                                            src={packageImage}
                                            alt={packageName}
                                            className="card-image"
                                        />
                                    ) : (
                                        <div
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                background: 'linear-gradient(135deg, #8B5CFB, #EC4899)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'white',
                                                fontSize: '16px',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            Catering Package
                                        </div>
                                    )}
                                    
                                    {/* Veg/Non-Veg Badge */}
                                    <div className="card-badge">
                                        {isVeg ? '🥗 Vegetarian' : isNonVeg ? '🍗 Non-Veg' : '🍽️ Package'}
                                    </div>
                                </div>

                                {/* Card Content */}
                                <div className="card-content">
                                    {/* Header with Title and Price */}
                                    <div className="card-header">
                                        <h3 className="card-title">
                                            {packageName}
                                        </h3>
                                        <div className="card-price">
                                            <i className="fa-solid fa-indian-rupee-sign" style={{ fontSize: "14px" }}></i>
                                            {packagePrice}
                                        </div>
                                    </div>

                                    {/* Description */}
                                    <p className="card-description">
                                        {packageDescription}
                                    </p>

                                    {/* Included Items */}
                                    {itemsList.length > 0 && (
                                        <div className="card-features">
                                            {itemsList.slice(0, 3).map((menuItem, index) => (
                                                <span key={index} className="feature-tag">
                                                    {menuItem.itemName}
                                                </span>
                                            ))}
                                            {itemsList.length > 3 && (
                                                <span className="feature-tag">
                                                    +{itemsList.length - 3} more
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="card-actions">
                                        {/* Info Button */}
                                        <div
                                            style={{
                                                width: "32px",
                                                height: "32px",
                                                borderRadius: "50%",
                                                backgroundColor: "#ee6521",
                                                color: "#fff",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "14px",
                                                cursor: "pointer",
                                                position: "relative"
                                            }}
                                            onClick={() => handleToggleTooltip(packageId, packageDescription)}
                                        >
                                            <Info style={{ fontSize: "18px" }} />
                                            {activeTooltip === packageId && (
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        top: "40px",
                                                        left: "0",
                                                        backgroundColor: "#fff",
                                                        color: "#333",
                                                        padding: "16px",
                                                        borderRadius: "12px",
                                                        fontSize: "14px",
                                                        fontWeight: "400",
                                                        lineHeight: "1.5",
                                                        boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                                                        whiteSpace: "normal",
                                                        width: "280px",
                                                        zIndex: 100,
                                                        border: "1px solid #e5e7eb",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            position: "absolute",
                                                            top: "-8px",
                                                            left: "12px",
                                                            width: "0",
                                                            height: "0",
                                                            borderLeft: "8px solid transparent",
                                                            borderRight: "8px solid transparent",
                                                            borderBottom: "8px solid #fff",
                                                        }}
                                                    ></div>
                                                    <h4
                                                        style={{
                                                            margin: "0 0 8px",
                                                            fontSize: "16px",
                                                            fontWeight: "600",
                                                            color: "#1f2937",
                                                        }}
                                                    >
                                                        {packageName}
                                                    </h4>
                                                    <p style={{ 
                                                        margin: 0, 
                                                        fontSize: "14px", 
                                                        color: "#6b7280",
                                                        lineHeight: "1.5"
                                                    }}>
                                                        {packageDescription}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Add to Cart / Quantity Controls */}
                                        {itemInCart ? (
                                            <div className="quantity-controls">
                                                <button
                                                    className="quantity-btn"
                                                    onClick={() => decrementQuantity(packageId)}
                                                >
                                                    -
                                                </button>
                                                <span className="quantity-display">
                                                    {itemQuantity}
                                                </span>
                                                <button
                                                    className="quantity-btn"
                                                    onClick={() => incrementQuantity(packageId)}
                                                >
                                                    +
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                className="add-to-cart-btn"
                                                onClick={() => addToCartDirectly(item)}
                                                disabled={loading}
                                            >
                                                {loading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                        Adding...
                                                    </>
                                                ) : (
                                                    'Add to Cart'
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Cart Modal */}
            <Modal
  isOpen={isModalOpen}
  onRequestClose={() => setIsModalOpen(false)}
  contentLabel="Cart Modal"
  shouldCloseOnOverlayClick={false}
  shouldCloseOnEsc={false}
  style={{
    overlay: {
      backgroundColor: "transparent",
      pointerEvents: "none",
    },
    content: {
      pointerEvents: "auto",
      position: "absolute",
      top: "92%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "80%",
      maxWidth: "500px",
      border: "none",
      boxShadow: "none",
      padding: "0px",
      height: "85px",
    },
  }}
>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 18px",
      width: "100%",
      borderRadius: "12px",
      height: "80px",
      border: "2px solid #ee6521",
      fontWeight: "600",
      color: "#333",
      background: "#fff"
    }}
  >

    {/* CART ICON */}
    <ShoppingCart
      style={{
        fontSize: "30px",
        color: "#ee6521",
        marginRight: "10px"
      }}
    />

    {/* ITEMS COUNT */}
    <div style={{ fontSize: "20px", marginRight: "10px" }}>
      ({miniCartItems.reduce((acc, item) =>
        acc + item.quantity, 0)
      }) Items
    </div>

    {/* TOTAL PRICE */}
    <div style={{ fontSize: "20px", marginRight: "10px" }}>
      Total: ₹
      {miniCartItems
        .reduce((acc, item) => acc + (item.price * item.quantity), 0)
        .toFixed(2)}
    </div>

    {/* VIEW CART BUTTON */}
    <button
      style={{
        borderRadius: "50px",
        background: "#ee6521",
        color: "#fff",
        padding: "8px 16px",
        border: "none",
        cursor: "pointer",
        whiteSpace: "nowrap",
        fontSize: "14px"
      }}
      onClick={handleViewCartClick}
    >
      View Cart
    </button>
  </div>
</Modal>



            {/* Floating Cart Button for Mobile */}
            {isMobile && (miniCartItems.length > 0 || Object.values(cartQuantities).some(qty => qty > 0)) && (
                <div
                    style={{
                        position: "fixed",
                        bottom: "20px",
                        right: "20px",
                        zIndex: 100,
                        background: "#ee6521",
                        color: "#fff",
                        borderRadius: "50%",
                        width: "60px",
                        height: "60px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 20px rgba(177, 93, 198, 0.4)",
                        cursor: "pointer"
                    }}
                    onClick={() => setIsModalOpen(true)}
                >
                    <ShoppingCart style={{ fontSize: "24px" }} />
                    {calculateTotalItems() > 0 && (
                        <div style={{
                            position: "absolute",
                            top: "-5px",
                            right: "-5px",
                            background: "#ef4444",
                            color: "#fff",
                            borderRadius: "50%",
                            width: "24px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                            fontWeight: "bold"
                        }}>
                            {calculateTotalItems()}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CateringItems;

